import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { usePetClaim } from '@/shared/hooks/queries/use-pet-claim'
import { usePetPolicyIds } from '@/shared/hooks/queries/use-pet-policy-ids'
import { usePetClaimDocuments } from '@/shared/hooks/queries/use-pet-claim-documents'
import { usePets } from '@/shared/hooks/queries/use-pets'
import { usePolicies } from '@/shared/hooks/queries/use-policies'
import { useUser } from '@/shared/hooks/use-user'
import { useUpdatePetClaim } from '@/shared/hooks/queries/use-update-pet-claim'
import { useIsOffline } from '@/shared/hooks/use-is-offline'
import { ApiProblemError } from '@/shared/helpers/api-problem'
import { formatAmountCents, toAmountCents } from '@/shared/helpers/amount-cents'
import { dataUrlToFile } from '@/shared/helpers/data-url-to-file'
import { formatApiDate, parseApiDate } from '@/shared/helpers/format-date'
import { HTTP_STATUS } from '@/shared/constants/api'
import { ROUTES } from '@/shared/constants/routes'
import {
  CLAIM_PROBLEM_TYPE,
  PET_CLAIM_ACCEPTED_FILE_TYPES,
  PET_CLAIM_DESCRIPTION_MAX_LENGTH,
  PET_CLAIM_PICTURE_TYPES,
  PET_CLAIM_PLEDGE_FILE_NAME,
} from '@/shared/constants/claims'
import { CLAIM_DOCUMENT_TYPE } from '@/shared/types/global.types'
import type { ClaimDocument } from '@/shared/types/global.types'
import {
  screenSelectedFiles,
  validateInvoiceFiles,
  validatePetPictureFile,
} from '@/shared/helpers/claim-files'
import { isClaimEditable } from '@/shared/helpers/claims-status'
import { buildPetClaimCard } from '@/shared/helpers/pet-claim-card'
import { useSubmitClaimModal } from '@/shared/hooks/use-submit-claim-modal'
import { useClaimFormSteps } from '@/shared/hooks/use-claim-form-steps'

const FORM_STEP_COUNT = 2

const STEP_ONE_FIELDS = ['invoiceDate', 'amount', 'description'] as const

export type EditClaimFormValues = {
  /** Cents, as a digit string */
  amount: string
  invoiceDate: Date | undefined
  description: string
  keptInvoices: ClaimDocument[]
  newInvoices: File[]
  petPicture: File | undefined
  signature: string | undefined
}

const API_FIELD_TO_FORM_FIELD: Record<string, keyof EditClaimFormValues> = {
  amount: 'amount',
  invoice_date: 'invoiceDate',
  description: 'description',
  invoices: 'newInvoices',
  keep_invoice_ids: 'keptInvoices',
  pledge_of_honor: 'signature',
  pet_picture: 'petPicture',
}

export function useClaimsEditPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isOffline = useIsOffline()
  const { claimId = '' } = useParams({ strict: false })
  const { user } = useUser()
  const { policyIds, isLoading: isPolicyIdsLoading } = usePetPolicyIds()
  const {
    data: claim,
    isLoading: isClaimLoading,
    isError,
  } = usePetClaim(claimId, policyIds)
  const isLoading = isPolicyIdsLoading || isClaimLoading
  // The claim itself doesn't carry its files; they come from /documents
  const { data: documents = [], isLoading: isDocumentsLoading } =
    usePetClaimDocuments(claimId)
  const currentInvoices = useMemo(
    () =>
      documents.filter(
        (document) => document.type === CLAIM_DOCUMENT_TYPE.INVOICE,
      ),
    [documents],
  )
  const currentPetPicture = documents.find(
    (document) => document.type === CLAIM_DOCUMENT_TYPE.PET_PICTURE,
  )
  const { data: policies = [], isLoading: isPoliciesLoading } = usePolicies()
  const { data: pets = [], isLoading: isPetsLoading } = usePets()
  const { currentStep, isFlipping, isLastStep, flipToStep, handleBack } =
    useClaimFormSteps(FORM_STEP_COUNT)
  const updateMutation = useUpdatePetClaim(claimId)
  // After saving: submit now, or go back to the claim and submit later
  const submitModal = useSubmitClaimModal({
    onCancel: (id) =>
      navigate({ to: ROUTES.CLAIM_DETAIL, params: { claimId: id } }),
    onFinish: () => navigate({ to: ROUTES.CLAIMS }),
  })
  const [submitErrorKey, setSubmitErrorKey] = useState<string | undefined>(
    undefined,
  )
  const hasResetRef = useRef(false)

  const formMethods = useForm<EditClaimFormValues>({
    defaultValues: {
      amount: '',
      invoiceDate: undefined,
      description: '',
      keptInvoices: [],
      newInvoices: [],
      petPicture: undefined,
      signature: undefined,
    },
    mode: 'all',
  })

  const {
    control,
    watch,
    setValue,
    getValues,
    trigger,
    setError,
    handleSubmit: formHandleSubmit,
    formState: { errors },
  } = formMethods

  useEffect(() => {
    if (!claim || isDocumentsLoading || hasResetRef.current) return
    hasResetRef.current = true
    formMethods.reset({
      amount: claim.amount ? String(claim.amount) : '',
      invoiceDate: parseApiDate(claim.invoice_date),
      description: claim.description ?? '',
      keptInvoices: currentInvoices,
      newInvoices: [],
      petPicture: undefined,
      signature: undefined,
    })
  }, [claim, isDocumentsLoading, currentInvoices, formMethods])

  // Submitting from the modal refetches the claim as filed: keep the page (and
  // its success modal) until the user leaves through it
  const isFiled =
    !!claim && !isClaimEditable(claim.status) && !submitModal.isOpen
  const hasPledge = documents.some(
    (document) => document.type === CLAIM_DOCUMENT_TYPE.PLEDGE_OF_HONOR,
  )

  const requiredRule = { required: t('common.required') }
  const rules = {
    amount: {
      ...requiredRule,
      validate: (value: string) =>
        Number(value) > 0 || t('claims.pet.form.amountError'),
    },
    // The API takes any invoice date that isn't in the future
    invoiceDate: requiredRule,
    // Required to submit: the insurance core won't file a claim without it
    description: {
      ...requiredRule,
      maxLength: {
        value: PET_CLAIM_DESCRIPTION_MAX_LENGTH,
        message: t('claims.pet.form.descriptionTooLong', {
          max: PET_CLAIM_DESCRIPTION_MAX_LENGTH,
        }),
      },
    },
    newInvoices: {
      validate: (files: File[]) =>
        validateInvoiceFiles(
          t,
          files,
          files.length + getValues('keptInvoices').length,
          [getValues('petPicture')],
        ),
    },
    petPicture: {
      validate: (file: File | undefined) => validatePetPictureFile(t, file),
    },
    // Only needed when the claim has no pledge of honor yet
    signature: hasPledge ? {} : requiredRule,
  }

  const fieldErrors = {
    amount: errors.amount?.message,
    invoiceDate: errors.invoiceDate?.message,
    description: errors.description?.message,
    invoices: errors.newInvoices?.message ?? errors.keptInvoices?.message,
    petPicture: errors.petPicture?.message,
    signature: errors.signature?.message,
  }

  function removeKeptInvoice(id: string) {
    setValue(
      'keptInvoices',
      getValues('keptInvoices').filter((invoice) => invoice.id !== id),
      { shouldDirty: true },
    )
    trigger('newInvoices')
  }

  function addNewInvoices(files: File[]) {
    const { accepted, rejectionMessage } = screenSelectedFiles(
      t,
      files,
      PET_CLAIM_ACCEPTED_FILE_TYPES,
    )
    setValue('newInvoices', [...getValues('newInvoices'), ...accepted], {
      shouldDirty: true,
      shouldValidate: true,
    })
    // Set after validating, or the passing validation would clear it
    if (rejectionMessage) setError('newInvoices', { message: rejectionMessage })
  }

  function removeNewInvoice(index: number) {
    setValue(
      'newInvoices',
      getValues('newInvoices').filter((_, fileIndex) => fileIndex !== index),
      { shouldDirty: true, shouldValidate: true },
    )
  }

  function selectPetPicture(file: File) {
    const { accepted, rejectionMessage } = screenSelectedFiles(
      t,
      [file],
      PET_CLAIM_PICTURE_TYPES,
    )
    if (rejectionMessage || !accepted.length) {
      setError('petPicture', { message: rejectionMessage })
      return
    }
    setValue('petPicture', file, { shouldDirty: true, shouldValidate: true })
  }

  const petPicture = watch('petPicture')
  const petPicturePreviewUrl = useMemo(
    () => (petPicture ? URL.createObjectURL(petPicture) : undefined),
    [petPicture],
  )
  useEffect(() => {
    return () => {
      if (petPicturePreviewUrl) URL.revokeObjectURL(petPicturePreviewUrl)
    }
  }, [petPicturePreviewUrl])

  const handleSubmitError = useCallback(
    (error: Error) => {
      if (!(error instanceof ApiProblemError)) {
        setSubmitErrorKey('claims.pet.errors.generic')
        return
      }
      switch (error.status) {
        case HTTP_STATUS.UNPROCESSABLE:
          Object.entries(error.fieldErrors).forEach(([apiField, detail]) => {
            const formField = API_FIELD_TO_FORM_FIELD[apiField]
            if (formField) setError(formField, { message: detail })
            if (
              (STEP_ONE_FIELDS as readonly string[]).includes(formField ?? '')
            ) {
              flipToStep(0)
            }
          })
          setSubmitErrorKey('claims.pet.errors.validation')
          return
        case HTTP_STATUS.CONFLICT:
          setSubmitErrorKey(
            error.problem.type === CLAIM_PROBLEM_TYPE.CLAIM_ALREADY_FILED
              ? 'claims.edit.errors.alreadyFiled'
              : 'claims.edit.errors.conflict',
          )
          return
        case HTTP_STATUS.PAYLOAD_TOO_LARGE:
          setSubmitErrorKey('claims.pet.errors.filesTooLarge')
          return
        case HTTP_STATUS.UNSUPPORTED_MEDIA_TYPE:
          setSubmitErrorKey('claims.pet.errors.unsupportedFile')
          return
        default:
          setSubmitErrorKey('claims.pet.errors.generic')
      }
    },
    [setError, flipToStep],
  )

  const handleSave = formHandleSubmit((values) => {
    if (!claim) return
    setSubmitErrorKey(undefined)
    // `invoices` replaces the whole set, so only send it when the set changed
    const hasInvoiceChanges =
      values.newInvoices.length > 0 ||
      values.keptInvoices.length !== currentInvoices.length
    updateMutation.mutate(
      {
        amount: Number(values.amount),
        invoiceDate: values.invoiceDate
          ? formatApiDate(values.invoiceDate)
          : '',
        description: values.description.trim(),
        ...(hasInvoiceChanges && {
          keepInvoiceIds: values.keptInvoices.map((invoice) => invoice.id),
          newInvoices: values.newInvoices,
        }),
        pledgeOfHonor: values.signature
          ? dataUrlToFile(values.signature, PET_CLAIM_PLEDGE_FILE_NAME)
          : undefined,
        petPicture: values.petPicture,
      },
      { onSuccess: submitModal.open, onError: handleSubmitError },
    )
  })

  async function handleNext() {
    if (!isLastStep) {
      const isStepValid = await trigger([...STEP_ONE_FIELDS])
      if (isStepValid) flipToStep(currentStep + 1)
      return
    }
    handleSave()
  }

  const policy = policies.find(
    (item) => String(item.policyExternalId) === String(claim?.policy_id),
  )
  const pet = pets.find((item) => item.id === policy?.petId)

  function handleCancel() {
    navigate({ to: ROUTES.CLAIM_DETAIL, params: { claimId } })
  }

  return {
    claimId,
    formMethods,
    control,
    rules,
    fieldErrors,
    isLoading:
      isLoading || isDocumentsLoading || isPoliciesLoading || isPetsLoading,
    isNotFound: !isLoading && (isError || !claim),
    currentStep,
    isFlipping,
    isLastStep,
    handleNext,
    handleBack,
    card: buildPetClaimCard(t, pet, policy, user?.fullName ?? ''),
    isFiled,
    hasPledge,
    currentPetPictureName: currentPetPicture?.file_name,
    keptInvoices: watch('keptInvoices'),
    removeKeptInvoice,
    addNewInvoices,
    removeNewInvoice,
    selectPetPicture,
    petPicturePreviewUrl,
    toAmountCents,
    formatAmountCents,
    today: new Date(),
    descriptionMaxLength: PET_CLAIM_DESCRIPTION_MAX_LENGTH,
    isSaving: updateMutation.isPending,
    submitErrorKey,
    isOffline,
    handleCancel,
    submitModal,
  }
}

export type ClaimsEditPageState = ReturnType<typeof useClaimsEditPage>
